import React, { FC } from 'react';
import { Button, Modal } from '@/components/ui';
import classNames from 'classnames';
import useLocale from '@/hooks/useLocale';
import { Settings } from 'react-slick';

import { TypeProduct, TypeImage, TypeVideo } from '@/core/generated-types';
import { BUTTON_STYLES } from '@/components/ui/Buttons/utils';
import ChevronIcon from '@/components/ui/Icons/ChevronIcon';
import { ShopifyProduct, ShopifyVariant } from '@/lib/shopify';
import { formatMoney } from '@/utils/helpers';
import { CarouselProducts } from '../Carousel';
import { ORGANIC_COTTON_BABY_DOLL_COLOR_MAP } from '@/utils/products/organicCottonBabyDollOptions';
import { useInventoryThreshold } from '@/hooks/useInventoryThreshold';
import { STANDALONE_THRESHOLD } from '@/components/subflow-v3/components/PlayKitDetailsHeader/PlayKitDetailsHeaderTreatment';
import Heading, {
  HeadingSize,
  HeadingTag,
} from '@/components/ui/Heading/Heading';

const getBackgroundColor = (title: string): string => {
  return ORGANIC_COTTON_BABY_DOLL_COLOR_MAP[title] || '';
};

const customSettings: Settings = {
  slidesToShow: 1,
  adaptiveHeight: false,
  dots: true,
  speed: 200,
  touchThreshold: 10,
  swipeToSlide: true,
  focusOnSelect: true,
  arrows: true,
  nextArrow: (
    <ChevronIcon
      classes="text-charcoal hover:text-charcoal mr-4 slick-arrow slick-next h-4 w-4"
      direction="right"
    />
  ),
  prevArrow: (
    <ChevronIcon
      classes="text-charcoal hover:text-charcoal ml-4 slick-arrow slick-prev h-4 w-4"
      direction="left"
    />
  ),
};

interface PlaythingsModalProps {
  isOpen: boolean;
  handleClose: () => void;
  product: TypeProduct;
  addProduct: () => void;
  shopifyProductAddOn: ShopifyProduct | null;
  selectedVariant?: ShopifyVariant | null;
  setSelectedVariant?: (option: ShopifyVariant) => void;
  priceCentsStandalone?: number;
}

const PlaythingsModal: FC<PlaythingsModalProps> = ({
  isOpen,
  handleClose,
  product,
  addProduct,
  shopifyProductAddOn,
  selectedVariant,
  setSelectedVariant,
  priceCentsStandalone,
}) => {
  const { title, shortDescription, addToCartText, productImages } =
    product.fields;
  const organicCottonBabyDollOptions = shopifyProductAddOn?.variants;
  const locale = useLocale();

  const { inventoryData } = useInventoryThreshold({
    productId: shopifyProductAddOn?.id,
    threshold: STANDALONE_THRESHOLD,
  });

  // Determine if the selected variant is out of stock (below threshold)
  const isSelectedVariantOOS =
    !!selectedVariant &&
    inventoryData?.some(
      (inventory) =>
        inventory.variantId === selectedVariant.id &&
        inventory.isBelowThreshold,
    );

  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      modalClasses={{
        mobile: {
          top: 'auto',
          bottom: '0',
          minWidth: '100vw',
          maxHeight: '90dvh',
          transform: 'translateX(-50%)',
        },
      }}
      overlayClasses={{
        backgroundColor: 'rgba(59,59,59,.75)',
      }}
    >
      <div className="container lg:gap-6 max-w-xs lg:grid lg:max-w-[936px] lg:grid-cols-2 lg:px-4 lg:pt-6 lg:pb-4">
        <div>
          <CarouselProducts
            name="Variant images carousel"
            classes="doll-modal-carousel"
            components={productImages as (TypeImage | TypeVideo)[]}
            id={product.sys.id}
            key={product.sys.id}
            customSettings={customSettings}
            showThumbnailSlider={false}
          />
        </div>
        <div>
          <div className="grid grid-cols-1 gap-2 mb-2 lg:gap-4">
            <div className="flex justify-between gap-20 lg:flex-col items-start lg:gap-4">
              <Heading
                tag={HeadingTag.H1}
                size={HeadingSize.H1}
                classes="inline lg:block"
                text={title}
                hoverEffect
              />
              {priceCentsStandalone && (
                <p className="text-lg lg:text-2xl font-bold block lg:hidden">
                  {formatMoney(priceCentsStandalone / 100, locale, false)}
                </p>
              )}
            </div>
            <p className="text-base">{shortDescription}</p>
            {priceCentsStandalone && (
              <p className="text-2xl font-bold hidden lg:block">
                {formatMoney(priceCentsStandalone / 100, locale, false)}
              </p>
            )}
          </div>
          <div className="mt-6 mb-4 border-y py-3 lg:border-b lg:border-t-0 border-grey-midtone lg:pb-3">
            <p className="mb-2 text-base">
              Choose a skin tone: {selectedVariant?.title}
            </p>
            <ul className="flex align-middle">
              {organicCottonBabyDollOptions?.map((option) => {
                const { id, title } = option;
                const isOrganicCottonBabyDollSkinToneSelected =
                  selectedVariant?.id === id;
                const backgroundColor = getBackgroundColor(title);

                return (
                  <li key={id} className="mr-2 text-center">
                    <div
                      role="button"
                      aria-label={title}
                      aria-controls="skin-tone-options"
                      data-testid="skin-tone-option"
                      className={classNames(
                        'cursor-pointer rounded-full border border-solid w-8 h-8',
                        isOrganicCottonBabyDollSkinToneSelected
                          ? 'outline outline-[1.5px] outline-charcoal outline-offset-[2px]'
                          : 'border-transparent',
                      )}
                      style={{ backgroundColor }}
                      onClick={() => {
                        if (setSelectedVariant !== undefined) {
                          setSelectedVariant(option);
                        }
                      }}
                    />
                  </li>
                );
              })}
            </ul>
          </div>

          <Button
            ctaText={
              isSelectedVariantOOS
                ? 'Out of Stock'
                : addToCartText || 'Add to Order'
            }
            onClick={() => {
              addProduct();
              handleClose();
            }}
            style={BUTTON_STYLES.primary}
            classes="mt-2 w-full h-[50px] lg:h-[60px]"
            disabled={isSelectedVariantOOS}
          />
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(PlaythingsModal);
